import * as THREE from "three"
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer"
// eslint-disable-next-line
import { gsap, Expo, Circ, Power0, Power1, Power2, Power3, Power4 } from "gsap"
import { FragShader } from "./glsl/tile.frag"
import { VertexShader } from "./glsl/tile.vert"
import { TILE_MOVEMENT_DIRECTION } from "./Gallery3D.Constants"

const IMAGE_SCALE = 0.004

export default class Tile extends THREE.Object3D {
  // eslint-disable-next-line
  constructor(data) {
    super()

    this.data = data

    //Set position
    this.position.y = data.position.y

    //Create animated image
    this.natureWidth = data.texture.image.width
    this.natureHeight = data.texture.image.height

    this.width = data.texture.image.width / 520
    this.height = data.texture.image.height / 520

    this.extra = 0

    this.uniforms = {
      uTexture: {
        type: "sampler2D",
        value: data.texture
      },
      uOpacity: {
        type: "float",
        value: 0
      },
      uOpacityHover: {
        type: "float",
        value: 0
      },
      uHoverProgress: {
        type: "float",
        value: 0
      },
      uDirection: {
        type: "f",
        value: 0
      },
      uIntensity: {
        type: "f",
        value: 0
      },
      uLimitCurve: {
        type: "f",
        value: 0.05
      },
      uLimitShear: {
        type: "f",
        value: 0.25
      },
      uColorBlend: {
        type: "f",
        value: 0.6
      },
      uOffsetNoise: {
        type: "vec2",
        value: [0, 1]
      },
      uColor: new THREE.Uniform(new THREE.Color(2965556))
    }

    const geometry = new THREE.PlaneBufferGeometry(1, 1, 8, 8)
    geometry.scale(this.width, this.height, 1)

    const material = new THREE.ShaderMaterial({
      vertexShader: VertexShader,
      fragmentShader: FragShader,
      uniforms: this.uniforms,
      side: THREE.DoubleSide,
      transparent: true,
      wireframe: false
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.castShadow = true
    this.plane = mesh
    this.add(mesh)

    //Title Annotation
    if (data.title) {
      const div = document.createElement("div")
      div.classList.add("annotation")
      const pivot = document.createElement("div")
      pivot.classList.add("pivot")
      div.append(pivot)
      this.titleAnno = document.createElement("span")
      this.titleAnno.classList.add("text")
      pivot.append(this.titleAnno)

      const annotation = new CSS2DObject(div)
      annotation.position.set(
        this.width * (-0.5 + data.title.position.x),
        this.height * (-0.5 + data.title.position.y),
        0
      )
      this.add(annotation)
    }

    //Caption Annotation
    if (data.caption) {
      const div = document.createElement("div")
      div.classList.add("annotation")
      const pivot = document.createElement("div")
      pivot.classList.add("pivot")
      div.append(pivot)
      this.captionAnno = document.createElement("span")
      this.captionAnno.classList.add("text")
      pivot.append(this.captionAnno)

      const annotation = new CSS2DObject(div)
      annotation.position.set(
        this.width * (-0.5 + data.caption.position.x),
        this.height * (-0.5 + data.caption.position.y),
        0
      )
      this.add(annotation)
    }

    //Title image
    if (data.titleImg) {
      console.log(data.titleImg)
      const div = document.createElement("div")
      div.classList.add("annotation")
      const pivot = document.createElement("div")
      pivot.classList.add("pivot")
      div.append(pivot)
      this.titleImgAnno = document.createElement("img")
      this.titleImgAnno.classList.add("title_image")

      pivot.append(this.titleImgAnno)

      const annotation = new CSS2DObject(div)
      annotation.position.set(
        this.width * (-0.5 + data.titleImg.position.x),
        this.height * (-0.5 + data.titleImg.position.y),
        0
      )
      this.add(annotation)
    }

    this.onAppear()
  }

  onAppear() {
    //Opacity anim
    gsap.killTweensOf(this.uniforms.uOpacity)
    gsap.to(this.uniforms.uOpacity, {
      value: 1,
      duration: 2,
      delay: 0,
      ease: Power1.easeInOut
    })

    //Wave&Color anim
    gsap.killTweensOf(this.uniforms.uHoverProgress)
    gsap.to(this.uniforms.uHoverProgress, {
      value: 1,
      duration: 2.2,
      delay: 0,
      ease: Power1.easeOut
    })

    //Appear annotation
    setTimeout(() => {
      if (this.titleAnno) {
        this.titleAnno.innerHTML = this.data.title.label
        this.titleAnno.classList.add("animate__animated", "animate__fadeInUp")
      }

      if (this.captionAnno) {
        this.captionAnno.innerHTML = this.data.caption.label
        this.captionAnno.classList.add("animate__animated", "animate__fadeInUp")
      }

      if (this.titleImgAnno) {
        this.titleImgAnno.src = this.data.titleImg.image
      }
    }, 1800)
  }

  onActive() {
    gsap.killTweensOf(this.uniforms.uHoverProgress)
    gsap.to(this.uniforms.uHoverProgress, {
      value: 1,
      duration: 0.8,
      delay: 0,
      ease: Power1.easeIn
    })

    if (this.titleAnno) {
      this.titleAnno.classList.remove("active")
    }

    if (this.captionAnno) {
      this.captionAnno.classList.remove("active")
    }
  }

  onInactive() {
    gsap.killTweensOf(this.uniforms.uHoverProgress)
    gsap.to(this.uniforms.uHoverProgress, {
      value: 0,
      duration: 0.8,
      delay: 0,
      ease: Power1.easeIn
    })
  }

  onPicked() {
    this.onActive()

    if (this.titleAnno) {
      this.titleAnno.classList.add("active")
    }
    if (this.captionAnno) {
      this.captionAnno.classList.add("active")
    }
  }

  autoFlowAndDragdrop(x, direction, viewport, galleryLength) {
    //X
    this.position.x =
      this.data.position.x - viewport.width / 2 + this.width / 2 - x * viewport.width - this.extra

    //
    const planeOffset = this.width / 2
    const viewportOffset = viewport.width / 2

    let isBefore = this.position.x + planeOffset < -viewportOffset
    let isAfter = this.position.x - planeOffset > viewportOffset

    if (direction === TILE_MOVEMENT_DIRECTION.down && isBefore) {
      this.extra -= galleryLength
      isBefore = false
      isAfter = false
    }

    if (direction === TILE_MOVEMENT_DIRECTION.up && isAfter) {
      this.extra += galleryLength
      isBefore = false
      isAfter = false
    }
  }

  adjustScaleForCentroid(viewport) {
    const start = viewport.width / 2
    const x = Math.abs(this.position.x)
    if (x < start) {
      const scale = 1 + (start - x) * IMAGE_SCALE
      this.scale.x = scale
      this.scale.y = scale
    }
  }

  animate(x, direction, viewport, galleryLength) {
    this.autoFlowAndDragdrop(x, direction, viewport, galleryLength)
    this.adjustScaleForCentroid(viewport)
  }
}
